import type { TTaskSourceType } from '@homepal/api-contract'
import { Color } from 'themes'
import { TranslateEnumFunction, useTranslation } from 'language/translator'
import { TDashboard } from 'types/GlobalDashboard'
import { useDashboard } from './Dashboards'
import { useDashboardGroup } from './DashboardGroups'
import { useAnalysis } from './Analyses'
import { useKeyMetric } from './KeyMetrics'
import { useReport, useReportEdition } from './Reports'

export type TGenericSource = {
  color?: Color
  href: string
  tab?: string
  variant: 'dashboard' | 'analysis' | 'key_metric' | 'report' | 'tasks'
  title: string
}

export const translateResourceTypeToTitle = (
  resourceType: TTaskSourceType,
  translateEnum: TranslateEnumFunction
) => {
  return translateEnum('source_types', resourceType)
}

export const useGenericSource = (
  resourceType: TTaskSourceType | null,
  resourceId: string | null
): TGenericSource => {
  const { t } = useTranslation()

  const EMPTY_GENERIC_SOURCE: TGenericSource = {
    href: '/tasks',
    title: t('tasks', 'tasks'),
    variant: 'tasks'
  }
  const dashboard = useDashboard(resourceId) as TDashboard | null
  const dashboardGroup = useDashboardGroup(
    dashboard?.dashboard_group_id || null
  )
  const keyMetric = useKeyMetric(
    resourceType === 'KEY_METRIC' ? resourceId : null
  )
  const edition = useReportEdition(
    resourceType === 'REPORT_EDITION' ? resourceId : null
  )
  const report = useReport(edition?.report_id ?? null)
  const { analysis } = useAnalysis(
    resourceType === 'ANALYSIS' ? resourceId : null
  )

  if (!resourceType || !resourceId) return EMPTY_GENERIC_SOURCE

  if (resourceType === 'ANALYSIS' && analysis) {
    return {
      href: `/analyses/${resourceId}`,
      title: analysis.title,
      variant: 'analysis'
    }
  }

  if (resourceType === 'DASHBOARD' && dashboard && dashboardGroup) {
    return {
      href: `/dashboards/${resourceId}`,
      tab: dashboard.title,
      title: dashboardGroup.title,
      variant: 'dashboard'
    }
  }

  if (resourceType === 'KEY_METRIC' && keyMetric) {
    return {
      href: `/key-metrics/${resourceId}`,
      title: keyMetric.title,
      variant: 'key_metric'
    }
  }

  if (resourceType === 'REPORT_EDITION' && edition && report) {
    return {
      href: `/reports/${edition.report_id}/editions/${resourceId}`,
      tab: edition.title ?? undefined,
      title: report.title,
      variant: 'report'
    }
  }

  return EMPTY_GENERIC_SOURCE
}
